import { useSyncExternalStore } from 'react'
import type { ConfiguredConnector, ConnectorConfig } from '@bloo-team/api-client'

/**
 * Demo connector writes — localStorage v1 (house rules as source-store.ts:
 * try/catch everywhere, SSR-safe, a corrupt or wrong-version blob reads as
 * empty and is superseded by the next write).
 *
 * THIS SHAPE IS A DRAFT API CONTRACT (docs/sources.md §Connectors · Demo):
 * ONE key per cluster+scope, `bloo.connectors.<clusterId>.<scopeId>`, holding
 *   { v: 1,
 *     added:   { [connector_id]: { row, config, at } },   demo `configure`
 *     edited:  { [connector_id]: { config, at } },        demo `edit_config`
 *     deleted: { [connector_id]: at } }                   demo `delete`
 * (epoch ms). Live mode never reads it.
 */

export interface ConnectorOverlay {
  v: 1
  added: Record<string, { row: ConfiguredConnector; config: ConnectorConfig; at: number }>
  edited: Record<string, { config: ConnectorConfig; at: number }>
  deleted: Record<string, number>
}

export const CONNECTOR_KEY_PREFIX = 'bloo.connectors.'
export const connectorOverlayKey = (clusterId: string, scopeId: string) => `${CONNECTOR_KEY_PREFIX}${clusterId}.${scopeId}`

const EMPTY: ConnectorOverlay = Object.freeze({ v: 1, added: {}, edited: {}, deleted: {} }) as ConnectorOverlay
const isObj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v)

function readOverlay(parsed: unknown): ConnectorOverlay {
  if (!isObj(parsed) || parsed.v !== 1) return EMPTY
  const added: ConnectorOverlay['added'] = {}
  for (const [id, e] of Object.entries(isObj(parsed.added) ? parsed.added : {})) {
    if (isObj(e) && isObj(e.row) && isObj(e.config) && typeof e.at === 'number') {
      added[id] = { row: e.row as ConfiguredConnector, config: e.config, at: e.at }
    }
  }
  const edited: ConnectorOverlay['edited'] = {}
  for (const [id, e] of Object.entries(isObj(parsed.edited) ? parsed.edited : {})) {
    if (isObj(e) && isObj(e.config) && typeof e.at === 'number') edited[id] = { config: e.config, at: e.at }
  }
  const deleted: ConnectorOverlay['deleted'] = {}
  for (const [id, at] of Object.entries(isObj(parsed.deleted) ? parsed.deleted : {})) {
    if (typeof at === 'number' && Number.isFinite(at)) deleted[id] = at
  }
  return { v: 1, added, edited, deleted }
}

export interface ConnectorOverlayStore {
  read: () => ConnectorOverlay
  add: (row: ConfiguredConnector, config: ConnectorConfig, at?: number) => void
  edit: (id: string, config: ConnectorConfig, at?: number) => void
  remove: (id: string, at?: number) => void
  subscribe: (cb: () => void) => () => void
}

class LocalConnectorStore implements ConnectorOverlayStore {
  private cache: ConnectorOverlay | null = null
  private listeners = new Set<() => void>()
  constructor(private readonly key: string) {}

  read = (): ConnectorOverlay => {
    if (this.cache) return this.cache
    let parsed: unknown
    try {
      const raw = globalThis.localStorage?.getItem(this.key)
      parsed = raw ? JSON.parse(raw) : null
    } catch {
      parsed = null
    }
    this.cache = readOverlay(parsed)
    return this.cache
  }

  add = (row: ConfiguredConnector, config: ConnectorConfig, at = Date.now()) => {
    const cur = this.read()
    this.write({ ...cur, added: { ...cur.added, [row.connector_id]: { row, config, at } } })
  }

  edit = (id: string, config: ConnectorConfig, at = Date.now()) => {
    const cur = this.read()
    const added = cur.added[id]
    if (added) {
      const name = typeof config.connector_name === 'string' ? config.connector_name : added.row.connector_name
      this.write({ ...cur, added: { ...cur.added, [id]: { row: { ...added.row, connector_name: name }, config, at } } })
      return
    }
    this.write({ ...cur, edited: { ...cur.edited, [id]: { config, at } } })
  }

  remove = (id: string, at = Date.now()) => {
    const cur = this.read()
    if (cur.added[id]) {
      const added = { ...cur.added }
      delete added[id]
      this.write({ ...cur, added })
      return
    }
    this.write({ ...cur, deleted: { ...cur.deleted, [id]: at } })
  }

  reset = () => {
    try {
      globalThis.localStorage?.removeItem(this.key)
    } catch {
      /* no storage */
    }
    this.cache = EMPTY
    this.notify()
  }

  subscribe = (cb: () => void) => {
    this.listeners.add(cb)
    return () => {
      this.listeners.delete(cb)
    }
  }

  private write(next: ConnectorOverlay) {
    try {
      globalThis.localStorage?.setItem(this.key, JSON.stringify(next))
    } catch {
      /* quota / no storage: keep the in-memory overlay */
    }
    this.cache = next
    this.notify()
  }

  private notify() {
    for (const cb of this.listeners) cb()
  }
}

const stores = new Map<string, LocalConnectorStore>()

export function connectorStore(clusterId: string, scopeId: string): ConnectorOverlayStore {
  const key = connectorOverlayKey(clusterId, scopeId)
  let s = stores.get(key)
  if (!s) {
    s = new LocalConnectorStore(key)
    stores.set(key, s)
  }
  return s
}

export function useConnectorOverlay(clusterId: string, scopeId: string): ConnectorOverlay {
  const s = connectorStore(clusterId, scopeId)
  return useSyncExternalStore(s.subscribe, s.read, s.read)
}

/** Base rows minus deleted, edited names applied, added rows appended (insertion order). */
export function applyConnectorOverlay(rows: ConfiguredConnector[], o: ConnectorOverlay): ConfiguredConnector[] {
  const out = rows
    .filter((r) => !(r.connector_id in o.deleted))
    .map((r) => {
      const e = o.edited[r.connector_id]
      const name = e && typeof e.config.connector_name === 'string' ? e.config.connector_name : null
      return name ? { ...r, connector_name: name } : r
    })
  for (const a of Object.values(o.added)) out.push(a.row)
  return out
}

export function overlayConfig(id: string, o: ConnectorOverlay): ConnectorConfig | null {
  return o.added[id]?.config ?? o.edited[id]?.config ?? null
}

export function resetAllConnectorOverlays(): void {
  try {
    const ls = globalThis.localStorage
    if (ls) {
      for (let i = ls.length - 1; i >= 0; i--) {
        const k = ls.key(i)
        if (k && k.startsWith(CONNECTOR_KEY_PREFIX)) ls.removeItem(k)
      }
    }
  } catch {
    /* no storage */
  }
  for (const s of stores.values()) s.reset()
}
